import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    Event,
    LobbyCheckStatus,
    PlayerGameRequestResult,
    RoomAccessibility,
    type LobbyCheckResult,
} from "@uno/protocol";
import { AppContext } from "../context";
import useStoreToast from "../store/useStoreToast";
import getPlayerGamePath from "../utils/PlayerGameNavigation";
import requestSocketAcknowledgement from "../utils/SocketAcknowledgement";

const useRoomNavigation = () => {
    const { socket } = React.useContext(AppContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showToast = useStoreToast((state) => state.show);

    const createRoom = React.useCallback((
        accessibility: RoomAccessibility = RoomAccessibility.Public,
    ): Promise<boolean> => {
        if(!socket?.connected) {
            return Promise.resolve(false);
        }

        return requestSocketAcknowledgement<PlayerGameRequestResult>(
            socket,
            (acknowledge) => {
                socket.emit(
                    Event.PlayerGameRequest,
                    { accessibility },
                    acknowledge,
                );
            },
        ).then((result) => {
            if(!result) {
                return false;
            }

            navigate(getPlayerGamePath(result));

            return true;
        });
    }, [socket, navigate]);

    const joinRoom = React.useCallback((roomId: string): Promise<boolean> => {
        if(!socket?.connected) {
            return Promise.resolve(false);
        }

        return requestSocketAcknowledgement<LobbyCheckResult>(
            socket,
            (acknowledge) => {
                socket.emit(
                    Event.PlayerCheckLobby,
                    roomId,
                    acknowledge,
                );
            },
        ).then((result) => {
            if(!result) {
                return false;
            }

            if(result.status === LobbyCheckStatus.NotFound) {
                showToast(t("lobby.not_found"), "error");

                return true;
            }

            navigate(
                result.status === LobbyCheckStatus.Started
                    ? `/room/${result.roomId}`
                    : `/lobby/${roomId}`,
            );

            return true;
        });
    }, [socket, navigate, showToast, t]);

    return { createRoom, joinRoom };
};

export default useRoomNavigation;
